import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Spinner,
  Row,
  Col,
  CardHeader,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Alert,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useGetPrimaryCompanyQuery,
  useGetPrimaryCompanyByIdQuery,
  useUpdatePrimaryCompanyMutation,
  useCreatePrimaryCompanyMutation,
} from "slices/pageAccess/PageAccessSlice";
import {
  useCreatePdfMetadataMutation,
  useGetPdfMetadataByIdQuery,
  useUpdatePdfMetadataMutation,
  useDeletePdfMetadataMutation,
  useGetPdfMetadataListQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { UncontrolledTooltip } from "reactstrap";

const validationSchema = Yup.object({
  title: Yup.string().required("Company name is required"),
  company_name: Yup.string().required("Display Name is required"),
  company_code: Yup.string().required("Company Code is required"),
  address1: Yup.string().required("Address 1 is required"),
  contact_number: Yup.string().required("Contact Number is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
});

// Interface for PDF metadata - Updated to match backend
interface PdfMetadata {
  id?: number;
  pdftype: string;
  metadata: Record<string, string>;
  // active?: boolean;
  company_id?: number;
}

// PDF Type options
const PDF_TYPES = [
  { value: "PurchaseRequest", label: "Purchase Request" },
  { value: "PurchaseOrder", label: "Purchase Order" },
  // { value: "PurchaseOrderFulfilment", label: "Purchase Order Fulfilment" },
  // { value: "PurchaseDelivery", label: "Purchase Delivery" },
];

const PrimaryCompanyTab: React.FC = () => {
  // ========== STATE DECLARATIONS ==========
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null,
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [pdfMetadataModalOpen, setPdfMetadataModalOpen] =
    useState<boolean>(false);
  const [editingMetadataId, setEditingMetadataId] = useState<number | null>(
    null,
  );

  const [logoLoading, setLogoLoading] = useState<boolean>(false);
  const [logominiLoading, setLogominiLoading] = useState<boolean>(false);
  const [headerPdf, setHeaderPdf] = useState<boolean>(false);
  const [footerPdf, setFooterPdf] = useState<boolean>(false);
  const [pdfMetadataLoading, setPdfMetadataLoading] = useState<boolean>(false);

  // ========== API HOOKS ==========
  const {
    data: primaryCompanyData,
    refetch,
    isLoading,
  } = useGetPrimaryCompanyQuery(undefined);
  const { data: companyDetails } = useGetPrimaryCompanyByIdQuery(
    selectedCompanyId,
    { skip: !selectedCompanyId },
  );

  // PDF Metadata queries and mutations
  const { data: pdfMetadataList, refetch: refetchPdfMetadata } =
    useGetPdfMetadataListQuery(undefined, {
      skip: !primaryCompanyData?.[0]?.id,
    });
  const { data: pdfMetadataDetail } = useGetPdfMetadataByIdQuery(
    editingMetadataId!,
    { skip: !editingMetadataId },
  );
  const [createPdfMetadata] = useCreatePdfMetadataMutation();
  const [updatePdfMetadata] = useUpdatePdfMetadataMutation();
  const [deletePdfMetadata] = useDeletePdfMetadataMutation();

  const [updatePrimaryCompany, { isLoading: companyLoading }] =
    useUpdatePrimaryCompanyMutation();
  const [createPrimaryCompany, { isLoading: postCompanyLoading }] =
    useCreatePrimaryCompanyMutation();
  const [logoError, setLogoError] = useState<string | null>(null);
  const [miniLogoError, setMiniLogoError] = useState<string | null>(null);
  const [pdfLogoError, setPdfLogoError] = useState<string | null>(null);
  const [pdfFooterLogoError, setPdfFooterLogoError] = useState<string | null>(
    null,
  );

  // State for PDF metadata form - Updated to store as metadata objects
  const [pdfMetadataFields, setPdfMetadataFields] = useState<
    {
      pdftype: string;
      metadata: Record<string, string>;
    }[]
  >([
    {
      pdftype: "PurchaseRequest",
      metadata: {},
    },
  ]);

  // ========== DEBUGGING ==========
  useEffect(() => {
    console.log("========== DEBUG INFO ==========");
    console.log("primaryCompanyData:", primaryCompanyData);
    console.log("Company ID:", primaryCompanyData?.[0]?.id);
    console.log("pdfMetadataList:", pdfMetadataList);
    console.log("pdfMetadataModalOpen:", pdfMetadataModalOpen);
    console.log("editingMetadataId:", editingMetadataId);
    console.log("pdfMetadataFields:", pdfMetadataFields);
    console.log("=================================");
  }, [
    primaryCompanyData,
    pdfMetadataList,
    pdfMetadataModalOpen,
    editingMetadataId,
    pdfMetadataFields,
  ]);

  // ========== HELPER FUNCTIONS ==========
  const toggleModal = () => setModalOpen(!modalOpen);
  const togglePdfMetadataModal = () =>
    setPdfMetadataModalOpen(!pdfMetadataModalOpen);

  // Get PDF type label
  const getPdfTypeLabel = (type: string) => {
    const found = PDF_TYPES.find((t) => t.value === type);
    return found ? found.label : type;
  };

  // ========== PDF METADATA HANDLERS ==========
  // Handle PDF metadata form changes - Updated for metadata object
  const handlePdfMetadataChange = (
    index: number,
    field: "pdftype" | "key" | "value",
    value: string,
  ) => {
    const updatedFields = [...pdfMetadataFields];

    if (field === "pdftype") {
      updatedFields[index].pdftype = value;
    } else {
      // For key and value, update the metadata object
      const currentMetadata = { ...updatedFields[index].metadata };

      if (field === "key") {
        // If key changes, preserve the value if exists
        const oldKey = Object.keys(currentMetadata)[0];
        const oldValue = currentMetadata[oldKey] || "";
        delete currentMetadata[oldKey];
        if (value.trim() !== "") {
          currentMetadata[value] = oldValue;
        }
      } else if (field === "value") {
        // Update the value for the current key
        const currentKey = Object.keys(currentMetadata)[0] || "key";
        currentMetadata[currentKey] = value;
      }

      updatedFields[index].metadata = currentMetadata;
    }

    setPdfMetadataFields(updatedFields);
    console.log(`Updated field ${index}:`, updatedFields[index]);
  };

  // Add new metadata field
  const addPdfMetadataField = () => {
    setPdfMetadataFields([
      ...pdfMetadataFields,
      {
        pdftype: "PurchaseRequest",
        metadata: {},
      },
    ]);
  };

  // Remove metadata field
  const removePdfMetadataField = (index: number) => {
    if (pdfMetadataFields.length > 1) {
      const updatedFields = pdfMetadataFields.filter((_, i) => i !== index);
      setPdfMetadataFields(updatedFields);
    }
  };

  // Save PDF metadata
  const handleSavePdfMetadata = async (companyId: number) => {
    console.log("========== SAVE PDF METADATA ==========");
    console.log("Company ID received:", companyId);
    console.log("PDF Metadata Fields to save:", pdfMetadataFields);

    setPdfMetadataLoading(true);
    try {
      // Filter out empty fields
      const validFields = pdfMetadataFields.filter(
        (field) =>
          Object.keys(field.metadata).length > 0 &&
          Object.values(field.metadata)[0]?.trim() !== "",
      );

      console.log("Valid fields (filtered):", validFields);

      if (validFields.length === 0) {
        console.warn("No valid fields to save");
        alert("Please fill at least one key-value pair");
        setPdfMetadataLoading(false);
        return;
      }

      // Save each metadata field
      for (let i = 0; i < validFields.length; i++) {
        const field = validFields[i];
        console.log(`Saving field ${i + 1}/${validFields.length}:`, field);

        // Transform data to match backend expectations
        const payload = {
          pdftype: field.pdftype,
          metadata: field.metadata,
        };

        console.log("API Payload:", payload);

        try {
          const result = await createPdfMetadata(payload).unwrap();
          console.log(`Field ${i + 1} saved successfully:`, result);
        } catch (fieldError) {
          console.error(`Error saving field ${i + 1}:`, fieldError);
          throw fieldError;
        }
      }

      console.log("All fields saved successfully");
      await refetchPdfMetadata();

      // Reset form
      setPdfMetadataFields([
        {
          pdftype: "PurchaseRequest",
          metadata: {},
        },
      ]);

      setPdfMetadataModalOpen(false);
    } catch (error: any) {
      console.error("========== ERROR SAVING PDF METADATA ==========");
      console.error("Error:", error);

      // Better error handling
      if (error?.data) {
        console.error("Error details:", error.data);
        alert(`Error: ${JSON.stringify(error.data)}`);
      } else {
        alert(
          `Error saving PDF metadata: ${error?.message || "Unknown error"}`,
        );
      }
    } finally {
      setPdfMetadataLoading(false);
    }
  };

  // Save button click handler
  const handleSaveMetadataClick = async () => {
    console.log("========== SAVE BUTTON CLICKED ==========");
    console.log("primaryCompanyData:", primaryCompanyData);

    const companyId = primaryCompanyData?.[0]?.id;
    console.log("Extracted Company ID:", companyId);

    if (!companyId) {
      console.error(
        "No company ID found! primaryCompanyData:",
        primaryCompanyData,
      );
      alert("No company found. Please add company information first.");
      return;
    }

    await handleSavePdfMetadata(companyId);
  };

  // Edit PDF metadata
  const handleEditPdfMetadata = (metadataId: number) => {
    console.log("Edit metadata clicked. ID:", metadataId);
    setEditingMetadataId(metadataId);
    setPdfMetadataModalOpen(true);
  };

  // Update PDF metadata
  const handleUpdatePdfMetadata = async () => {
    console.log("========== UPDATE PDF METADATA ==========");
    console.log("Editing Metadata ID:", editingMetadataId);
    console.log("PDF Metadata Detail:", pdfMetadataDetail);
    console.log("Update fields:", pdfMetadataFields[0]);

    if (!editingMetadataId) {
      console.error("No editingMetadataId found");
      return;
    }

    setPdfMetadataLoading(true);
    try {
      const field = pdfMetadataFields[0];
      const payload = {
        pdftype: field.pdftype,
        metadata: field.metadata,
      };

      console.log("Update payload:", payload);

      const result = await updatePdfMetadata({
        id: editingMetadataId,
        data: payload,
      }).unwrap();

      console.log("Update result:", result);
      await refetchPdfMetadata();

      setEditingMetadataId(null);
      setPdfMetadataFields([
        {
          pdftype: "PurchaseRequest",
          metadata: {},
        },
      ]);
      setPdfMetadataModalOpen(false);
    } catch (error: any) {
      console.error("========== ERROR UPDATING PDF METADATA ==========");
      console.error("Error:", error);
      alert(
        `Error updating PDF metadata: ${error?.message || "Unknown error"}`,
      );
    } finally {
      setPdfMetadataLoading(false);
    }
  };

  // Delete PDF metadata
  const handleDeletePdfMetadata = async (metadataId: number) => {
    console.log("Delete metadata clicked. ID:", metadataId);

    if (window.confirm("Are you sure you want to delete this metadata?")) {
      try {
        console.log("Deleting metadata ID:", metadataId);
        const result = await deletePdfMetadata(metadataId).unwrap();
        console.log("Delete result:", result);

        await refetchPdfMetadata();
        console.log("PDF metadata refetched after delete");
      } catch (error: any) {
        console.error("Error deleting PDF metadata:", error);
        alert(
          `Error deleting PDF metadata: ${error?.message || "Unknown error"}`,
        );
      }
    }
  };

  // ========== LOGO UPLOAD HANDLERS ==========
  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    companyId: number,
  ) => {
    setLogoLoading(true);

    const file = e.target.files?.[0];
    if (file) {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (event) => {
        img.src = event.target?.result as string;
      };

      img.onload = async () => {
        // Validate dimensions
        if (img.width !== 304 || img.height !== 86) {
          setLogoError("Image dimensions must be 304x86 pixels.");
          setLogoLoading(false);
          return;
        }

        try {
          const formData = new FormData();
          formData.append("logo", file);
          await updatePrimaryCompany({
            id: companyId,
            updateData: formData,
          }).unwrap();
          setLogoError("");
          refetch();
          setLogoLoading(false);
        } catch (error) {
          console.error("Error uploading logo:", error);
          setLogoLoading(false);
        }
      };

      reader.readAsDataURL(file);
    }
  };

  // company_logo_mini
  const handleminilogoFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    companyId: number,
  ) => {
    setLogominiLoading(true);
    const file = e.target.files?.[0];
    if (file) {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (event) => {
        img.src = event.target?.result as string;
      };

      img.onload = async () => {
        // Validate dimensions
        if (img.width !== 458 || img.height !== 461) {
          setMiniLogoError("Image dimensions must be 458x461 pixels.");
          setLogominiLoading(false);
          return;
        }

        try {
          const formData = new FormData();
          formData.append("company_logo_mini", file);
          await updatePrimaryCompany({
            id: companyId,
            updateData: formData,
          }).unwrap();
          setMiniLogoError("");
          refetch();
          setLogominiLoading(false);
        } catch (error) {
          console.error("Error uploading logo:", error);
          setLogominiLoading(false);
        }
      };

      reader.readAsDataURL(file);
    }
  };

  // pdf_report_logo
  const handlepdflogoFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    companyId: number,
  ) => {
    setHeaderPdf(true);
    const file = e.target.files?.[0];
    if (file) {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (event) => {
        img.src = event.target?.result as string;
      };

      img.onload = async () => {
        // Validate dimensions
        if (img.width !== 1088 || img.height !== 179) {
          setPdfLogoError("Image dimensions must be 1088x179 pixels.");
          setHeaderPdf(false);
          return;
        }

        try {
          const formData = new FormData();
          formData.append("pdf_report_logo", file);
          await updatePrimaryCompany({
            id: companyId,
            updateData: formData,
          }).unwrap();
          setPdfLogoError("");
          refetch();
          setHeaderPdf(false);
        } catch (error) {
          console.error("Error uploading logo:", error);
          setHeaderPdf(false);
        }
      };

      reader.readAsDataURL(file);
    }
  };

  const handlefooterlogoFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    companyId: number,
  ) => {
    setFooterPdf(true);
    const file = e.target.files?.[0];
    if (file) {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (event) => {
        img.src = event.target?.result as string;
      };

      img.onload = async () => {
        // Validate dimensions
        if (img.width !== 1025 || img.height !== 110) {
          setPdfFooterLogoError("Image dimensions must be 1025x110 pixels.");
          setFooterPdf(false);
          return;
        }

        try {
          const formData = new FormData();
          formData.append("pdf_footer_logo", file);
          await updatePrimaryCompany({
            id: companyId,
            updateData: formData,
          }).unwrap();
          setPdfFooterLogoError("");
          refetch();
          setFooterPdf(false);
        } catch (error) {
          console.error("Error uploading logo:", error);
          setFooterPdf(false);
        }
      };

      reader.readAsDataURL(file);
    }
  };

  // Delete functions
  const handleDeleteLogo = async (companyId: number) => {
    try {
      setLogoLoading(true);
      const formData = new FormData();
      formData.append("clear_logo", "true");
      await updatePrimaryCompany({
        id: companyId,
        updateData: formData,
      }).unwrap();
      setLogoError(null);
      refetch();
      setLogoLoading(false);
    } catch (error) {
      console.error("Error deleting logo:", error);
      setLogoLoading(false);
    }
  };

  const handleDeleteMiniLogo = async (companyId: number) => {
    try {
      setLogominiLoading(true);
      const formData = new FormData();
      formData.append("clear_company_logo_mini", "true");
      await updatePrimaryCompany({
        id: companyId,
        updateData: formData,
      }).unwrap();
      setMiniLogoError(null);
      refetch();
      setLogominiLoading(false);
    } catch (error) {
      console.error("Error deleting mini logo:", error);
      setLogominiLoading(false);
    }
  };

  const handleDeletePdfLogo = async (companyId: number) => {
    try {
      setHeaderPdf(true);
      const formData = new FormData();
      formData.append("clear_pdf_report_logo", "true");
      await updatePrimaryCompany({
        id: companyId,
        updateData: formData,
      }).unwrap();
      setPdfLogoError(null);
      refetch();
      setHeaderPdf(false);
    } catch (error) {
      console.error("Error deleting PDF logo:", error);
      setHeaderPdf(false);
    }
  };

  const handleDeletePdfFooterLogo = async (companyId: number) => {
    try {
      setFooterPdf(true);
      const formData = new FormData();
      formData.append("clear_pdf_footer_logo", "true");
      await updatePrimaryCompany({
        id: companyId,
        updateData: formData,
      }).unwrap();
      setPdfFooterLogoError(null);
      refetch();
      setFooterPdf(false);
    } catch (error) {
      console.error("Error deleting PDF footer logo:", error);
      setFooterPdf(false);
    }
  };

  // ========== FORMIK ==========
  const formik = useFormik({
    initialValues: {
      title: "",
      company_name: "",
      company_code: "",
      address1: "",
      address2: "",
      country: "",
      zip_code: "",
      contact_number: "",
      email: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (selectedCompanyId) {
        await updatePrimaryCompany({
          id: selectedCompanyId,
          updateData: values,
        }).unwrap();
        refetch();
        toggleModal();
      } else {
        await createPrimaryCompany({
          newPrimaryCompany: values,
        }).unwrap();
        refetch();
        toggleModal();
      }
    },
  });

  // ========== EFFECTS ==========
  // Effect to set form values when companyDetails changes
  useEffect(() => {
    if (companyDetails) {
      formik.setValues({
        title: companyDetails.title || "",
        company_name: companyDetails.company_name || "",
        company_code: companyDetails.company_code || "",
        address1: companyDetails.address1 || "",
        address2: companyDetails.address2 || "",
        country: companyDetails.country || "",
        zip_code: companyDetails.zip_code || "",
        contact_number: companyDetails.contact_number || "",
        email: companyDetails.email || "",
      });
    }
  }, [companyDetails]);

  // Load metadata data when editing
  useEffect(() => {
    if (pdfMetadataDetail && editingMetadataId) {
      setPdfMetadataFields([
        {
          pdftype: pdfMetadataDetail.pdftype || "PurchaseRequest",
          metadata: pdfMetadataDetail.metadata || {},
        },
      ]);
    }
  }, [pdfMetadataDetail, editingMetadataId]);

  // Reset metadata form when modal closes
  useEffect(() => {
    if (!pdfMetadataModalOpen) {
      setPdfMetadataFields([
        {
          pdftype: "PurchaseRequest",
          metadata: {},
        },
      ]);
      setEditingMetadataId(null);
    }
  }, [pdfMetadataModalOpen]);

  // ========== DATA PROCESSING ==========
  // Normalize API response safely (handles DRF pagination)
  const rawPdfMetadataList: PdfMetadata[] = Array.isArray(pdfMetadataList)
    ? pdfMetadataList
    : (pdfMetadataList as any)?.results || [];

  // Filter + group metadata by pdftype
  const groupedMetadata = rawPdfMetadataList
    // .filter((meta: PdfMetadata) => meta.active !== false)
    .reduce<Record<string, PdfMetadata[]>>((acc, meta) => {
      const key = meta.pdftype;
      if (!acc[key]) acc[key] = [];
      acc[key].push(meta);
      return acc;
    }, {});

  // ========== COMPANY HANDLERS ==========
  const handleEditCompany = (companyId: number) => {
    setSelectedCompanyId(companyId);
    toggleModal();
  };

  const handleAddCompany = () => {
    setSelectedCompanyId(null);
    toggleModal();
  };

  // ========== RENDER ==========
  return (
    <div className="container mt-4">
      <h5 className="mb-4">Company Profile</h5>
      {isLoading ? (
        <Spinner />
      ) : (
        <Row>
          {primaryCompanyData?.length === 0 && (
            <Button
              onClick={() => handleAddCompany()}
              className="btn btn-soft-info btn-md shadow-none"
            >
              Add Company Information
            </Button>
          )}

          {primaryCompanyData?.length > 0 ? (
            primaryCompanyData.map((c: any) => (
              <Col md={10} sm={8} key={c.id} className="mb-4">
                <Card>
                  <CardHeader className="d-flex align-items-center">
                    <i className="ri-building-fill fs-4"></i>
                    <h4 className="card-title flex-grow-1 mb-0">
                      {" "}
                      Primary Company
                    </h4>
                    <div className="flex-shrink-0">
                      <Button
                        onClick={() => handleEditCompany(c.id!)}
                        className="btn btn-soft-info btn-md shadow-none"
                      >
                        Edit
                      </Button>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <table className="table mb-0">
                      <tbody>
                        <tr>
                          <td className="fw-medium">Company Title</td>
                          <td>{c.title}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Display Name</td>
                          <td>{c.company_name}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Company Code</td>
                          <td>{c.company_code}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Address 1</td>
                          <td>{c.address1}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Address 2</td>
                          <td>{c.address2}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Country</td>
                          <td>{c.country}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Zip Code</td>
                          <td>{c.zip_code}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Contact Number</td>
                          <td>{c.contact_number}</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Email</td>
                          <td>{c.email}</td>
                        </tr>
                      </tbody>
                    </table>
                  </CardBody>

                  <hr></hr>
                  <h5 className="mt-3">Company Logo</h5>
                  <span className="text-danger">{logoError}</span>
                  <div className="border-top p-3 bg-light">
                    <div className="text-center">
                      <label
                        htmlFor={`logo-upload-${c.id}`}
                        className="btn btn-link p-0"
                        style={{ cursor: "pointer" }}
                      >
                        {companyLoading && logoLoading ? (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="spinner-border text-danger"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <small className="d-block mt-2">Uploading...</small>
                          </div>
                        ) : c.logo_url ? (
                          <div className="d-flex flex-column align-items-center">
                            <img
                              src={c.logo_url}
                              alt="Company Logo"
                              className="img-fluid mb-2"
                              style={{ maxHeight: "100px" }}
                            />
                            <span className="d-block btn btn-danger">
                              Click to change logo
                            </span>
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="bg-white rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: "60px", height: "60px" }}
                            >
                              <i className="ri-add-fill fs-4 text-muted"></i>
                            </div>
                            <small className="d-block mt-2">Upload Logo</small>
                          </div>
                        )}
                      </label>
                      {c.logo_url && (
                        <div>
                          <Button
                            color="danger"
                            size="sm"
                            className="mt-2"
                            onClick={() => handleDeleteLogo(c.id)}
                          >
                            Delete Company Logo
                          </Button>
                        </div>
                      )}
                      <input
                        id={`logo-upload-${c.id}`}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleFileUpload(e, c.id)}
                      />
                    </div>
                  </div>

                  <hr></hr>
                  <h5 className="mt-3">Company Mini-Logo</h5>
                  <span className="text-danger">{miniLogoError}</span>
                  <div className="border-top p-3 bg-light mt-2 ">
                    <div className="text-center">
                      <label
                        htmlFor={`minilogo-upload-${c.id}`}
                        className="btn btn-link p-0"
                        style={{ cursor: "pointer" }}
                      >
                        {companyLoading && logominiLoading ? (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="spinner-border text-danger"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <small className="d-block mt-2">Uploading...</small>
                          </div>
                        ) : c.company_logo_mini_url ? (
                          <div className="d-flex flex-column align-items-center">
                            <img
                              src={c.company_logo_mini_url}
                              alt="Company Mini Logo"
                              className="img-fluid mb-2"
                              style={{ maxHeight: "100px" }}
                            />
                            <span className="d-block btn btn-danger">
                              Click to change mini logo
                            </span>
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="bg-white rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: "60px", height: "60px" }}
                            >
                              <i className="ri-add-fill fs-4 text-muted"></i>
                            </div>
                            <small className="d-block mt-2">Upload Logo</small>
                          </div>
                        )}
                      </label>
                      {c.company_logo_mini_url && (
                        <div>
                          <Button
                            color="danger"
                            size="sm"
                            className="mt-2"
                            onClick={() => handleDeleteMiniLogo(c.id)}
                          >
                            Delete Mini Logo
                          </Button>
                        </div>
                      )}
                      <input
                        id={`minilogo-upload-${c.id}`}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleminilogoFileUpload(e, c.id)}
                      />
                    </div>
                  </div>

                  <hr></hr>
                  <h5 className="mt-3">Company Pdf Header Logo</h5>
                  <span className="text-danger">{pdfLogoError}</span>
                  <div className="border-top p-3 bg-light mt-2 ">
                    <div className="text-center">
                      <label
                        htmlFor={`pdflogo-upload-${c.id}`}
                        className="btn btn-link p-0"
                        style={{ cursor: "pointer" }}
                      >
                        {companyLoading && headerPdf ? (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="spinner-border text-danger"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <small className="d-block mt-2">Uploading...</small>
                          </div>
                        ) : c.pdf_report_logo_url ? (
                          <>
                            <img
                              src={c.pdf_report_logo_url}
                              alt="Company Pdf Header Logo"
                              className="img-fluid mb-2"
                              style={{ maxHeight: "100px" }}
                            />
                            <span className="d-block btn btn-danger">
                              Click to change PDF Header logo
                            </span>
                          </>
                        ) : (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="bg-white rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: "60px", height: "60px" }}
                            >
                              <i className="ri-add-fill fs-4 text-muted"></i>
                            </div>
                            <small className="d-block mt-2">Upload Logo</small>
                          </div>
                        )}
                      </label>
                      {c.pdf_report_logo_url && (
                        <div>
                          <Button
                            color="danger"
                            size="sm"
                            className="mt-2"
                            onClick={() => handleDeletePdfLogo(c.id)}
                          >
                            Delete PDF Header Logo
                          </Button>
                        </div>
                      )}
                      <input
                        id={`pdflogo-upload-${c.id}`}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handlepdflogoFileUpload(e, c.id)}
                      />
                    </div>
                  </div>

                  <hr></hr>
                  <h5 className="mt-3">Company Pdf Footer Logo</h5>
                  <span className="text-danger">{pdfFooterLogoError}</span>
                  <div className="border-top p-3 bg-light mt-2 ">
                    <div className="text-center">
                      <label
                        htmlFor={`logofooter-upload-${c.id}`}
                        className="btn btn-link p-0"
                        style={{ cursor: "pointer" }}
                      >
                        {companyLoading && footerPdf ? (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="spinner-border text-danger"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <small className="d-block mt-2">Uploading...</small>
                          </div>
                        ) : c.pdf_footer_logo_url ? (
                          <div className="d-flex flex-column align-items-center">
                            <img
                              src={c.pdf_footer_logo_url}
                              alt="Company Footer Logo"
                              className="img-fluid mb-2"
                              style={{ maxHeight: "100px" }}
                            />
                            <span className="d-block btn btn-danger">
                              Click to change PDF Footer logo
                            </span>
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center">
                            <div
                              className="bg-white rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: "60px", height: "60px" }}
                            >
                              <i className="ri-add-fill fs-4 text-muted"></i>
                            </div>
                            <small className="d-block mt-2">Upload Logo</small>
                          </div>
                        )}
                      </label>
                      {c.pdf_footer_logo_url && (
                        <div>
                          <Button
                            color="danger"
                            size="sm"
                            className="mt-2"
                            onClick={() => handleDeletePdfFooterLogo(c.id)}
                          >
                            Delete PDF Footer Logo
                          </Button>
                        </div>
                      )}
                      <input
                        id={`logofooter-upload-${c.id}`}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handlefooterlogoFileUpload(e, c.id)}
                      />
                    </div>
                  </div>

                  {/* PDF Meta Field Section */}
                  <hr></hr>
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <h5 className="mb-0">PDF Meta Field</h5>
                    <Button
                      onClick={togglePdfMetadataModal}
                      className="btn btn-soft-info btn-sm shadow-none"
                    >
                      <i className="ri-add-fill"></i> Add Meta Field
                    </Button>
                  </div>
                  <div className="border-top p-3 bg-light mt-2">
                    {pdfMetadataList &&
                    Object.keys(groupedMetadata).length > 0 ? (
                      Object.entries(groupedMetadata).map(
                        ([pdfType, metadataList]) => (
                          <div key={pdfType} className="mb-4">
                            <h6 className="mb-2 text-primary">
                              {getPdfTypeLabel(pdfType)}
                            </h6>
                            <div className="row">
                              {metadataList.map((meta: PdfMetadata) => (
                                <Col md={12} key={meta.id} className="mb-3">
                                  <div className="bg-white p-3 rounded border">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <div>
                                        {Object.entries(meta.metadata).map(
                                          ([key, value]) => (
                                            <div key={key}>
                                              <strong className="d-block">
                                                {key}
                                              </strong>
                                              <span className="text-muted">
                                                {value}
                                              </span>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                      <div>
                                        <Button
                                          color="info"
                                          size="sm"
                                          className="me-2"
                                          onClick={() =>
                                            handleEditPdfMetadata(meta.id!)
                                          }
                                        >
                                          <i className="ri-edit-line"></i>
                                        </Button>
                                        <Button
                                          color="danger"
                                          size="sm"
                                          onClick={() =>
                                            handleDeletePdfMetadata(meta.id!)
                                          }
                                        >
                                          <i className="ri-delete-bin-line"></i>
                                        </Button>
                                      </div>
                                    </div>
                                    <small className="text-muted">
                                      PDF Type: {getPdfTypeLabel(meta.pdftype)}
                                    </small>
                                  </div>
                                </Col>
                              ))}
                            </div>
                          </div>
                        ),
                      )
                    ) : (
                      <div className="text-center py-4">
                        <i className="ri-file-list-3-line fs-1 text-muted"></i>
                        <p className="mt-2 text-muted">
                          No PDF metadata added yet
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            ))
          ) : (
            <Col>
              <p className="text-center mt-5">
                No Company Information Added Yet
              </p>
            </Col>
          )}
        </Row>
      )}

      {/* Existing Company Edit/Add Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {!selectedCompanyId ? "Add" : "Edit"} Company
        </ModalHeader>
        <ModalBody>
          <form onSubmit={formik.handleSubmit}>
            {(
              Object.keys(formik.initialValues) as Array<
                keyof typeof formik.values
              >
            ).map((key) => (
              <FormGroup key={key}>
                <Label for={key}>
                  {key === "title"
                    ? "Company Title"
                    : key === "company_name"
                      ? "Display Name"
                      : key
                          .replace("_", " ")
                          .replace(/\b\w/g, (char) => char.toUpperCase())}{" "}
                  {(key === "title" ||
                    key === "company_name" ||
                    key === "company_code" ||
                    key === "address1" ||
                    key === "contact_number" ||
                    key === "email") && <span className="text-danger">*</span>}
                  {key === "company_name" && (
                    <>
                      <i
                        id="displayNameTooltip"
                        className="ri-information-fill p-2 ms-1 text-secondary"
                        style={{ cursor: "pointer" }}
                      ></i>
                      <UncontrolledTooltip
                        placement="top"
                        target="displayNameTooltip"
                        innerClassName="custom-tooltip"
                      >
                        Display Name is used to show in the dropdown for all
                        Permits and Reports.
                      </UncontrolledTooltip>
                    </>
                  )}
                  {key === "company_code" && (
                    <>
                      <i
                        id="companyCodeTooltip"
                        className="ri-information-fill p-2 ms-1 text-secondary"
                        style={{ cursor: "pointer" }}
                      ></i>
                      <UncontrolledTooltip
                        placement="top"
                        target="companyCodeTooltip"
                        innerClassName="custom-tooltip"
                      >
                        Company Code is used in Permits and Reports to generate
                        the number sequence. <br /> <strong>Note:</strong> Any
                        changes may affect the Permits and Reports number
                        sequence.
                      </UncontrolledTooltip>
                    </>
                  )}
                </Label>
                <Input
                  id={key}
                  name={key}
                  value={formik.values[key]}
                  onChange={formik.handleChange}
                />
                {formik.errors[key] && formik.touched[key] && (
                  <div className="text-danger">{formik.errors[key]}</div>
                )}
              </FormGroup>
            ))}
            <ModalFooter>
              <Button
                color="primary"
                type="submit"
                disabled={postCompanyLoading || companyLoading}
              >
                Save
              </Button>
              <Button
                color="secondary"
                onClick={toggleModal}
                disabled={postCompanyLoading || companyLoading}
              >
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalBody>
      </Modal>

      {/* PDF Metadata Modal */}
      <Modal
        isOpen={pdfMetadataModalOpen}
        toggle={togglePdfMetadataModal}
        size="lg"
      >
        <ModalHeader toggle={togglePdfMetadataModal}>
          {editingMetadataId ? "Edit PDF Metadata" : "Add PDF Metadata"}
        </ModalHeader>
        <ModalBody>
          <Alert color="info" className="mb-3">
            <i className="ri-information-line me-2"></i>
            Add key-value pairs that will be displayed in PDF documents. Select
            the PDF type for each field.
          </Alert>

          {pdfMetadataFields.map((field, index) => (
            <div key={index} className="mb-3 p-3 border rounded">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Field {index + 1}</h6>
                {pdfMetadataFields.length > 1 && (
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => removePdfMetadataField(index)}
                  >
                    <i className="ri-delete-bin-line"></i>
                  </Button>
                )}
              </div>
              <Row>
                <Col md={12} className="mb-3">
                  <FormGroup>
                    <Label for={`pdf-type-${index}`}>PDF Type</Label>
                    <Input
                      id={`pdf-type-${index}`}
                      type="select"
                      value={field.pdftype}
                      onChange={(e) =>
                        handlePdfMetadataChange(
                          index,
                          "pdftype",
                          e.target.value,
                        )
                      }
                    >
                      {PDF_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for={`key-${index}`}>Key</Label>
                    <Input
                      id={`key-${index}`}
                      value={Object.keys(field.metadata)[0] || ""}
                      onChange={(e) =>
                        handlePdfMetadataChange(index, "key", e.target.value)
                      }
                      placeholder="Enter key (e.g., Document ID, Version)"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for={`value-${index}`}>Value</Label>
                    <Input
                      id={`value-${index}`}
                      value={Object.values(field.metadata)[0] || ""}
                      onChange={(e) =>
                        handlePdfMetadataChange(index, "value", e.target.value)
                      }
                      placeholder="Enter value"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </div>
          ))}

          <div className="text-center mt-3">
            <Button color="info" onClick={addPdfMetadataField} className="me-2">
              <i className="ri-add-line me-1"></i> Add Another Field
            </Button>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={
              editingMetadataId
                ? handleUpdatePdfMetadata
                : handleSaveMetadataClick
            }
            disabled={pdfMetadataLoading}
          >
            {pdfMetadataLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : editingMetadataId ? (
              "Update"
            ) : (
              "Save"
            )}
          </Button>
          <Button
            color="secondary"
            onClick={togglePdfMetadataModal}
            disabled={pdfMetadataLoading}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PrimaryCompanyTab;
